import { Rnd } from "react-rnd";
import { useRef, useState } from "react";
import { useOutletTables } from "./useTable";
import axios from "axios";
import { toast } from "react-toastify";

const baseUrl = process.env.REACT_APP_BASE_URL;

export default function TableLayoutEditor({ selectedOutlet, token }) {
    const {
        tablesWithCoordinates,
        allTableIdsUnion,
        setTablesWithCoordinates,
        rawTableWithCoordinates,
        load
    } = useOutletTables(selectedOutlet);

    const floorRef = useRef(null);
    const [newTableId, setNewTableId] = useState("");

    // ---- helper to update table coordinates ----
    const updatePosition = (id, x, y) => {
        setTablesWithCoordinates(prev =>
            prev.map(t => (t.id === id ? { ...t, x, y } : t))
        );
    };

    // ---- add new table with default position ----
    const addTable = () => {
        const id = newTableId.trim();

        if (!id) {
            toast.error("Enter table id");
            return;
        }

        const exists = tablesWithCoordinates.some(
            t => String(t.id).toLowerCase() === id.toLowerCase()
        );

        if (exists) {
            toast.error("Table ID already exists on floor");
            return;
        }

        let x = 44;
        let y = 44;

        if (floorRef.current) {
            const rect = floorRef.current.getBoundingClientRect();
            x = rect.width / 2 - 20;
            y = rect.height / 2 - 20;
        }

        setTablesWithCoordinates(prev => [
            ...prev,
            { id, x, y }
        ]);

        setNewTableId("");
    };

    // ---- delete table from coordinates ----
    const deleteTable = (id) => {
        setTablesWithCoordinates(prev => prev.filter(t => t.id !== id));
    };

    // ---- save coordinates ----
    const saveLayout = async () => {
        if (!selectedOutlet) {
            toast.error("Select outlet first");
            return;
        }

        const width = 1000
        const height = 550;

        const payload = {
            token,
            outlet_name: selectedOutlet,
            tables: tablesWithCoordinates.map(t => ({
                table_no: t.id,
                x_coordinate: Number((t.x / width).toFixed(4)),
                y_coordinate: Number((t.y / height).toFixed(4)),
            })),
        };

        try {
            const res = await axios.post(
                `${baseUrl}/save-table-coordinates`,
                payload
            );

            toast.success(
                res.data.message || "Table coordinates saved successfully"
            );

            load();
        } catch (err) {
            console.error(err);
            toast.error(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Failed to save table coordinates"
            );
        }
    };

    return (
        <div className="px-4 py-3 shadow-lg rounded-xl mt-2.5 max-w-7xl mx-auto">
            <h1 className="text-lg font-semibold text-center text-gray-800 mb-2">
                Table Layout Editor
            </h1>

            <div className="flex justify-center gap-8">
                <div>
                    <div className="flex gap-2 items-center mb-3">
                        <input
                            type="text"
                            placeholder="New Table ID"
                            value={newTableId}
                            onChange={e => setNewTableId(e.target.value)}
                            className="border p-2 text-sm rounded w-28"
                        />

                        <button
                            onClick={addTable}
                            className="px-3 py-2 rounded-md font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md text-xs whitespace-nowrap"
                        >
                            Add Table
                        </button>
                    </div>

                    <div className="w-52 bg-white/70 backdrop-blur-md border border-gray-200 
                        rounded-lg shadow-lg p-3 max-h-[550px] overflow-hidden text-center">

                        <h4 className="text-base font-semibold text-gray-800 tracking-wide">
                            All Tables
                        </h4>

                        <div className="space-y-1 overflow-y-auto max-h-[450px] tiny-scrollbar">
                            {allTableIdsUnion.map(t => (
                                <div
                                    key={t.id}
                                    className="p-2 text-sm rounded-lg font-medium text-gray-700 
                                        transition-all duration-200
                                        hover:bg-blue-50 hover:text-blue-600 hover:shadow-sm"
                                >
                                    {t.id}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div
                    ref={floorRef}
                    className="relative w-[1000px] h-[550px] border bg-gray-50 overflow-hidden"
                >
                    {tablesWithCoordinates.map(table => {
                        const existsInRaw = rawTableWithCoordinates?.some(
                            raw => String(raw.table_no ?? raw.id) === String(table.id)
                        );

                        return (
                            <Rnd
                                key={table.id}
                                bounds="parent"
                                size={{ width: 44, height: 44 }}
                                position={{ x: table.x, y: table.y }}
                                enableResizing={false}
                                onDragStop={(e, d) =>
                                    updatePosition(table.id, d.x, d.y)
                                }
                            >
                                <div
                                    className={`relative w-full h-full text-xs text-white rounded-lg 
                                        flex items-center justify-center shadow cursor-move select-none
                                        ${existsInRaw ? "bg-blue-600" : "bg-yellow-500"}`}
                                >
                                    {table.id}

                                    <button
                                        onClick={() => deleteTable(table.id)}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white 
                                            text-[10px] rounded-full w-5 h-5"
                                    >
                                        ×
                                    </button>
                                </div>
                            </Rnd>
                        );
                    })}
                </div>
            </div>

            <div className="flex justify-end">
                <button
                    onClick={saveLayout}
                    className="px-4 py-1.5 rounded-md font-medium bg-green-200 text-green-800 hover:bg-green-300 hover:shadow-md text-sm"
                >
                    Save Layout
                </button>
            </div>
        </div>
    );
}
